import {
  IBlock,
  IBlockDocuments,
  IBlockFounders,
  IBlockHistory,
  IBlockImage,
  IBlockImageSlider,
  IBlockInfoCards,
  IBlockInstitutions,
  IBlockLink,
  IBlockPersons,
  IBlockQuote,
  IBlockRichText,
  IBlockRichTextHighlight,
  IBlockSources,
  IBlockSpoilers,
  IBlockTable,
  IBlockVideo,
  IBlockWorkgroups,
  IHistoryPointDocument,
} from '@/interfaces/blocks'
import { IFile, IImage } from '@/interfaces/common'
import { mapFileFields, mapImageFields } from '@/adapters/common'
import { mapFounderFields } from '@/adapters/founders'
import { mapPersonFields } from '@/adapters/persons'
import { mapWorkgroupPreviewFields } from '@/adapters/workgroups'
import { mapInfoCardFields } from '@/adapters/infocards'
import { mapInstitutionFields } from '@/adapters/institutions'
import { IDocument } from '@/interfaces/documents'
import { mapDocumentFields } from '@/adapters/documents'
import { IFounder } from '@/interfaces/founders'
export function mapBlockRichTextFields(block: Directus.BlockRichText): IBlockRichText | null {
  const { translations, ...rest } = block.item
  if (!translations?.[0]?.content) return null
  return { type: block.collection, data: { ...rest, ...translations[0], content: translations[0].content } }
}

export function mapBlockRichTextHighlightFields(
  block: Directus.BlockRichTextHighlight
): IBlockRichTextHighlight | null {
  const { translations, ...rest } = block.item
  if (!translations?.[0]?.content) return null
  return { type: block.collection, data: { ...rest, ...translations[0], content: translations[0].content } }
}

export function mapBlockTableFields(block: Directus.BlockTable): IBlockTable {
  const { translations, ...rest } = block.item
  return { type: block.collection, data: { ...rest, ...translations[0] } }
}

export function mapBlockQuoteFields(block: Directus.BlockQuote): IBlockQuote {
  const { translations, ...rest } = block.item
  return { type: block.collection, data: { ...rest, ...translations[0] } }
}

export function mapBlockImageFields(block: Directus.BlockImage, locale?: string): IBlockImage | null {
  const { translations, image, image_en, has_fullscreen_mode } = block.item
  const actialImage = locale === 'ru-RU' ? image : image_en || image
  if (!actialImage) return null
  const imageMapped = mapImageFields(actialImage) as IImage
  return { type: block.collection, data: { ...translations[0], image: imageMapped, has_fullscreen_mode } }
}

export function mapBlockFoundersFields(block: Directus.BlockFounders, locale?: string): IBlockFounders {
  const { translations, items } = block.item
  const itemsMapped = (items
    ?.filter(item => !!item?.founder)
    ?.map(item => {
      return mapFounderFields(item.founder, locale)
    })
    ?.filter(Boolean) || []) as IFounder[]
  return { type: block.collection, data: { ...translations[0], items: itemsMapped } }
}

export function mapBlockPersonsFields(block: Directus.BlockPersons): IBlockPersons {
  const { translations, items, highlight_first } = block.item
  const itemsMapped = items
    .filter(item => !!item?.person)
    .map(item => {
      const person = mapPersonFields(item.person)
      return { role: item.translations?.[0]?.role || person.position, person }
    })
  return { type: block.collection, data: { ...translations[0], items: itemsMapped, highlight_first } }
}

export function mapBlockWorkgroupFields(block: Directus.BlockWorkgroups): IBlockWorkgroups {
  const { translations, items } = block.item
  const itemsMapped = items
    .filter(item => !!item?.workgroup)
    .map(item => {
      return mapWorkgroupPreviewFields(item.workgroup)
    })
  return { type: block.collection, data: { ...translations[0], items: itemsMapped } }
}

export function mapBlockInfoCardsFields(block: Directus.BlockInfoCards): IBlockInfoCards {
  const { items } = block.item
  const itemsMapped = items
    .filter(item => !!item?.info_card)
    .map(item => {
      return mapInfoCardFields(item.info_card)
    })
  return { type: block.collection, data: { items: itemsMapped } }
}

export function mapBlockSpoilersFields(block: Directus.BlockSpoilers): IBlockSpoilers {
  const { translations, items, visible_item_count } = block.item
  const itemsMapped = items
    .filter(item => !!item?.info_card)
    .map(item => {
      return mapInfoCardFields(item.info_card)
    })
  return { type: block.collection, data: { ...translations[0], items: itemsMapped, visible_item_count } }
}

export function mapBlockHistoryFields(block: Directus.BlockHistory): IBlockHistory {
  const { items } = block.item
  const itemsMapped = items
    .filter(item => !!item?.history_point)
    .map(item => {
      return {
        year: item.history_point.year,
        ...item.history_point.translations[0],
        documents: item.history_point.documents
          ?.filter(docItem => !!docItem.file)
          ?.map(docItem => {
            return {
              ...docItem.translations[0],
              file: mapFileFields(docItem.file) as IFile,
            }
          }) as IHistoryPointDocument[],
      }
    })
  return { type: block.collection, data: { items: itemsMapped } }
}

export function mapBlockInstitutionsFields(block: Directus.BlockInstitutions, locale?: string): IBlockInstitutions {
  const { translations, items } = block.item
  const itemsMapped = items
    .filter(item => !!item?.institution)
    .map(item => {
      return mapInstitutionFields(item.institution, locale)
    })
  return { type: block.collection, data: { ...translations[0], items: itemsMapped } }
}

export function mapBlockLinkFields(block: Directus.BlockLink): IBlockLink {
  const { translations, type, url, page, post, event } = block.item

  if (type === 'external' && url) {
    return { type: block.collection, data: { ...translations[0], url, type } }
  }

  if (type === 'page' && page?.slug) {
    return { type: block.collection, data: { ...translations[0], url: `/${page.slug}`, type } }
  }

  if (type === 'post' && post?.slug) {
    return { type: block.collection, data: { ...translations[0], url: `/analytics/${post.slug}`, type } }
  }

  if (type === 'event' && event?.slug) {
    return { type: block.collection, data: { ...translations[0], url: `/events/${event.slug}`, type } }
  }

  return { type: block.collection, data: { ...translations[0], url: '/', type: 'page' } }
}

export function mapBlockDocumentsFields(block: Directus.BlockDocuments, locale?: string): IBlockDocuments {
  const { items } = block.item
  const itemsMapped = items
    .filter(item => !!item?.document)
    .map(item => {
      return mapDocumentFields(item.document as Directus.Document, locale)
    })
  return { type: block.collection, data: { items: itemsMapped as IDocument[] } }
}

export function mapBlockImageSliderFields(block: Directus.BlockImageSlider): IBlockImageSlider {
  const { images, translations } = block.item
  const imagesMapped = images
    .filter(item => !!item?.file)
    .map(item => {
      return mapImageFields(item.file as Directus.Image)
    })
  return {
    type: block.collection,
    data: {
      images: imagesMapped as IImage[],
      ...translations?.[0],
    },
  }
}

export function mapBlockVideoFields(block: Directus.BlockVideo): IBlockVideo {
  return { type: block.collection, data: { code: block.item.code || '' } }
}

export function mapBlockSourcesFields(block: Directus.BlockSources): IBlockSources {
  return {
    type: block.collection,
    data: {
      items:
        block.item.items
          ?.filter(i => typeof i.translations?.[0]?.content === 'string')
          ?.map(i => ({ ...i.translations[0] }) as { content: string }) || [],
    },
  }
}

export const blockMapper = new Map<string, (block: Directus.Block, locale?: string) => IBlock | null>([
  ['block_rich_text', mapBlockRichTextFields],
  ['block_rich_text_highlight', mapBlockRichTextHighlightFields],
  ['block_table', mapBlockTableFields],
  ['block_quote', mapBlockQuoteFields],
  ['block_image', mapBlockImageFields],
  ['block_founders', mapBlockFoundersFields],
  ['block_founders', mapBlockFoundersFields],
  ['block_persons', mapBlockPersonsFields],
  ['block_workgroups', mapBlockWorkgroupFields],
  ['block_info_cards', mapBlockInfoCardsFields],
  ['block_spoilers', mapBlockSpoilersFields],
  ['block_history', mapBlockHistoryFields],
  ['block_institutions', mapBlockInstitutionsFields],
  ['block_link', mapBlockLinkFields],
  ['block_documents', mapBlockDocumentsFields],
  ['block_image_slider', mapBlockImageSliderFields],
  ['block_video', mapBlockVideoFields],
  ['block_sources', mapBlockSourcesFields],
])
